import { getMyNotifications, markAllNotificationsRead } from "@/api/modules";
import { EventCountdownHeader } from "@/features/events";
import { JSX, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    FaBoxOpen,
    FaBuilding,
    FaChartLine,
    FaCog,
    FaFileAlt,
    FaHardHat,
    FaLifeRing,
    FaNetworkWired,
    FaShoppingCart,
    FaTachometerAlt,
    FaTruck,
    FaUsers,
    FaWallet,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import { useAppContext } from "../../contexts/AppContext";

interface HeaderProps {
  title: string;
  description?: string;
  onMobileMenuToggle: () => void;
  user?: {
    name: string;
    email: string;
    avatarUrl?: string;
  };
  activeEvent?: any;
  onEventClick?: () => void;
}

// Icon mapping for different routes - sync with SidebarNav
const getRouteIcon = (pathname: string) => {
  const route = pathname.split("/").filter(Boolean).pop() || "dashboard";

  const iconMap: { [key: string]: JSX.Element } = {
    dashboard: <FaTachometerAlt className="h-5 w-5 text-primary" />,
    products: <FaBoxOpen className="h-5 w-5 text-primary" />, // Using FaBoxOpen to match sidebar
    analytics: <FaChartLine className="h-5 w-5 text-primary" />,
    orders: <FaShoppingCart className="h-5 w-5 text-primary" />,
    donations: <FaHardHat className="h-5 w-5 text-primary" />,
    users: <FaUsers className="h-5 w-5 text-primary" />,
    commissions: <FaWallet className="h-5 w-5 text-primary" />,
    emergency: <FaLifeRing className="h-5 w-5 text-primary" />,
    dispatch: <FaTruck className="h-5 w-5 text-primary" />,
    payments: <FaWallet className="h-5 w-5 text-primary" />,
    logs: <FaFileAlt className="h-5 w-5 text-primary" />,
    "team-network": <FaNetworkWired className="h-5 w-5 text-primary" />,
    "business-plan": <FaBuilding className="h-5 w-5 text-primary" />,
    profile: <FaUsers className="h-5 w-5 text-primary" />,
    referrals: <FaNetworkWired className="h-5 w-5 text-primary" />,
    "manage-payments": <FaWallet className="h-5 w-5 text-primary" />,
    "dispatch-management": <FaTruck className="h-5 w-5 text-primary" />,
    "admin-analytics": <FaChartLine className="h-5 w-5 text-primary" />,
  };

  // Handle compound routes
  if (pathname.includes("emergency")) return iconMap.emergency;
  if (pathname.includes("commission")) return iconMap.commissions;
  if (pathname.includes("order")) return iconMap.orders;
  if (pathname.includes("donation")) return iconMap.donations;
  if (pathname.includes("user") || pathname.includes("profile"))
    return iconMap.users;
  if (pathname.includes("referral")) return iconMap.referrals;
  if (pathname.includes("payment")) return iconMap.payments;

  return iconMap[route] || <FaCog className="h-5 w-5 text-primary" />;
};

const Header = ({
  title,
  description,
  onMobileMenuToggle,
  user,
  activeEvent,
  onEventClick,
}: HeaderProps) => {
  const { t } = useTranslation();
  const { setUser } = useAppContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    getMyNotifications({ limit: 8, onlyUnread: false }).then(res => {
      if (res.success && res.data) {
        const list: any[] = (res.data as any).notifications ?? res.data ?? [];
        setNotifications(list.slice(0, 8));
        setUnreadCount(list.filter((n: any) => !n.isRead).length);
      }
    }).catch(() => {});
  }, [user]);

  const handleMarkAll = async () => {
    await markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  // Close dropdown on outside click
  // (simple version, can be improved with useEffect for event listener)
  const handleDropdownBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropdownOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setUser(null); // Clear user state immediately
    navigate(ROUTES.LOGIN);
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate(ROUTES.PROFILE);
  };


  return (
    <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-4 lg:px-6">
      <div className="flex items-center justify-between">
        {/* Mobile menu button - only show on small and medium screens */}
        <div className="flex items-center lg:hidden">
          <button
            onClick={onMobileMenuToggle}
            className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
            aria-label={t("navigation.toggleMenu", "Toggle menu")}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        {/* Page Title with Icon */}
        <div className="flex-1 lg:flex-none">
          <div className="flex items-center gap-3 lg:gap-4 ml-2 lg:ml-4">
            <div className="w-10 h-10 lg:w-12 lg:h-12 bg-primary/10 rounded-xl flex items-center justify-center border border-primary/20 shadow-sm">
              {getRouteIcon(location.pathname)}
            </div>
            <div>
              <h1 className="text-lg lg:text-2xl font-bold text-gray-900 truncate">
                {title}
              </h1>
              {description && (
                <p className="text-xs lg:text-sm text-gray-600 mt-1 hidden sm:block">
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Event Countdown Timer - Fixed Header */}
        {activeEvent && onEventClick && (
          <div className="flex-1 flex justify-center mx-4">
            <EventCountdownHeader event={activeEvent} onClick={onEventClick} />
          </div>
        )}

        {/* User Profile & Controls */}
        {user && (
          <div className="flex items-center gap-2 lg:gap-3">
            {/* Notification Bell */}
            <div
              className="relative"
              ref={bellRef}
              tabIndex={0}
              onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setBellOpen(false); }}
            >
              <button
                onClick={() => setBellOpen(v => !v)}
                className="relative p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                aria-label={t("notifications.bell", "Notifications")}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {bellOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl w-80 z-50">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{t("notifications.tab", "Notifications")}</p>
                    <div className="flex items-center gap-3">
                      {unreadCount > 0 && (
                        <button onClick={handleMarkAll} className="text-xs text-primary hover:underline font-medium">
                          {t("notifications.markAllRead", "Mark all read")}
                        </button>
                      )}
                      <button
                        onClick={() => { setBellOpen(false); navigate(ROUTES.NOTIFICATIONS); }}
                        className="text-xs text-gray-500 hover:text-gray-900"
                      >
                        {t("common.viewAll", "View all")}
                      </button>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">{t("notifications.empty", "No notifications")}</div>
                    ) : (
                      notifications.map((n: any) => (
                        <div
                          key={n._id}
                          className={`px-4 py-3 flex gap-3 cursor-pointer hover:bg-gray-50 ${!n.isRead ? "bg-primary/5" : ""}`}
                          onClick={() => { setBellOpen(false); navigate(ROUTES.NOTIFICATIONS); }}
                        >
                          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.isRead ? "bg-primary" : "bg-transparent"}`}></div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${!n.isRead ? "font-semibold text-gray-900" : "text-gray-700"}`}>{n.title}</p>
                            {n.message && <p className="text-xs text-gray-500 truncate">{n.message}</p>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="px-4 py-2 border-t border-gray-100">
                    <button
                      onClick={() => { setBellOpen(false); navigate(ROUTES.NOTIFICATIONS); }}
                      className="w-full text-center text-xs text-primary hover:underline font-medium py-1"
                    >
                      {t("notifications.viewAll", "See all notifications →")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Dropdown */}
            <div
              className="relative"
              tabIndex={0}
              ref={dropdownRef}
              onBlur={handleDropdownBlur}
            >
              <button
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                onClick={() => setDropdownOpen((v) => !v)}
                tabIndex={0}
              >
                <img
                  src={user.avatarUrl || "/avatar-placeholder.png"}
                  alt={user.name}
                  className="w-8 h-8 rounded-full border-2 border-gray-200 bg-white object-cover"
                />
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                    {user.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate max-w-[120px]">
                    {user.email}
                  </p>
                </div>
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] z-50">
                  <div className="py-1">
                    {/* User info on mobile */}
                    <div className="px-4 py-3 border-b border-gray-100 md:hidden">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user.email}
                      </p>
                    </div>

                    {/* Profile */}
                    <button
                      className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={handleProfileClick}
                    >
                      <svg
                        className="w-4 h-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      {t("common.header.profile", "Profile")}
                    </button>

                    {/* Logout */}
                    <button
                      className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-100 transition-colors"
                      onClick={handleLogout}
                    >
                      <svg
                        className="w-4 h-4 text-red-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      {t("common.header.logout", "Logout")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
